import type { FC } from "react"
import { useEffect, useMemo, useRef, useState } from "react"

import type { ViewerImage } from "@productlane/ui"
import { cn } from "@productlane/lib/src/cn"
import { highlightCodeBlocksHtml } from "@productlane/lib/src/highlight-code"
import { ImageViewer } from "@productlane/ui"

import { useIsDarkMode } from "@/components/reporting/charts/use-dark-mode"
import {
	collectExpandableImages,
	EMAIL_SHADOW_STYLES,
	emailBodyClassName,
	hideBrokenImage,
	isExpandableImage,
	markExpandableImages,
	sanitizeEmailHtml,
} from "@/lib/email-html"

interface Props {
	html: string
	className?: string
	/**
	 * True for bodies composed in Productlane. They render with the composer's
	 * spacing; a sender's own HTML keeps whatever their mail client wrote.
	 */
	authoredInProductlane?: boolean
}

interface ViewerState {
	images: ViewerImage[]
	index: number
	originRect: DOMRect
}

export const EmailHtml: FC<Props> = ({
	html,
	className,
	authoredInProductlane = false,
}) => {
	const hostRef = useRef<HTMLDivElement | null>(null)
	const bodyRef = useRef<HTMLDivElement | null>(null)
	const [viewer, setViewer] = useState<ViewerState | null>(null)
	const isDark = useIsDarkMode()

	const sanitized = useMemo(
		() => highlightCodeBlocksHtml(sanitizeEmailHtml(html, { isDark })),
		[html, isDark],
	)

	useEffect(() => {
		const host = hostRef.current
		if (!host) return
		if (!bodyRef.current) {
			const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" })
			const style = document.createElement("style")
			style.textContent = EMAIL_SHADOW_STYLES
			const body = document.createElement("div")
			shadow.replaceChildren(style, body)
			bodyRef.current = body
		}
		bodyRef.current.className = emailBodyClassName(authoredInProductlane)
		bodyRef.current.innerHTML = sanitized
		markExpandableImages(bodyRef.current)
	}, [sanitized, authoredInProductlane])

	useEffect(() => {
		const body = bodyRef.current
		if (!body) return

		const onClick = (e: Event) => {
			const target = e.target
			if (!(target instanceof HTMLImageElement)) return
			if (!isExpandableImage(target)) return
			const images = collectExpandableImages(body)
			const index = images.indexOf(target)
			if (index === -1) return
			setViewer({
				images: images.map((img) => ({
					src: img.src,
					alt: img.alt,
					name: img.alt || undefined,
				})),
				index,
				originRect: target.getBoundingClientRect(),
			})
		}

		// Sizes are only known once an image loads; re-mark on each load (capture, load doesn't bubble).
		const onLoad = (e: Event) => {
			if (e.target instanceof HTMLImageElement) markExpandableImages(body)
		}

		const onError = (e: Event) => {
			if (e.target instanceof HTMLImageElement) hideBrokenImage(e.target)
		}

		body.addEventListener("click", onClick)
		body.addEventListener("load", onLoad, true)
		body.addEventListener("error", onError, true)
		return () => {
			body.removeEventListener("click", onClick)
			body.removeEventListener("load", onLoad, true)
			body.removeEventListener("error", onError, true)
		}
	}, [])

	return (
		<>
			<div
				ref={hostRef}
				data-theme={isDark ? "dark" : "light"}
				className={cn(
					"min-w-full select-text text-gray-800 dark:text-gray-300",
					className,
				)}
			/>
			{viewer !== null && (
				<ImageViewer
					images={viewer.images}
					index={viewer.index}
					originRect={viewer.originRect}
					onIndexChange={(index) => setViewer((v) => (v ? { ...v, index } : v))}
					onClose={() => setViewer(null)}
				/>
			)}
		</>
	)
}
